/**
 * TVLA Report main component.
 * Rendered as part of Reports -> TVLA Report tab.
 *
 * - Two date pickers (From Date / To Date) with default and min/max rules.
 * - Pie chart of TVLA requests broken down by (dynamic) VIN generation, excluding
 *   requests in 'Cancelled' status, for the inclusive from/to date range.
 * - Download button (top right) exports the underlying TVLA details as a CSV.
 */
import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';

import moment from 'moment';

import Papa from 'papaparse';

import { Typography, IconButton, Tooltip } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { makeStyles } from '@mui/styles';

import axios from '../../../../common/AxiosConfig';

import { AppContext } from '../../../../../RoutesWithAuth';

import { LoadingIndicator } from '../reconcilationreport/MuiStyled';

import TvlaPieChart from './TvlaPieChart';

// From Date cannot be older than this date.
const MIN_FROM_DATE = '2026-06-01';

const useStyles = makeStyles(() => ({
    wrapper: {
        position: 'relative',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #dedfdf',
        marginRight: '10px',
        padding: '10px 0px 5px 10px',
    },
    headerTitle: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#212529',
    },
    downloadIcon: {
        border: '1px solid #cfd8e3',
        borderRadius: '6px',
        background: '#f5f7fa',
        '&:hover': {
            background: '#e6edf5',
        },
        '&.Mui-disabled': {
            opacity: 0.5,
        },
    },
    filterRow: {
        display: 'flex',
        gap: '24px',
        margin: '14px 0 0 10px',
    },
    dateField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    dateLabel: {
        fontSize: '0.78rem',
        fontWeight: 700,
        color: '#212529',
    },
    dateInput: {
        padding: '6px 8px',
        fontSize: '0.82rem',
        border: '1px solid #cfd8e3',
        borderRadius: '4px',
        outline: 'none',
        '&:focus': {
            borderColor: '#267db7',
        },
    },
    chartWrapper: {
        height: '520px',
        marginTop: '10px',
    },
    note: {
        margin: '4px 0 20px 10px',
        fontSize: '0.82rem',
        fontWeight: 600,
        color: '#212529',
    },
}));

// Parse the /reports/tvla response `chart` array into nivo pie data.
// Generations are dynamic; we only render slices with a positive count.
// Unknown, null and empty generation values are grouped into a single 'Others' slice.
const toChartData = (chart) => {
    const normalizeGeneration = (generation) => {
        const value = (generation ?? '').toString().trim();
        if (!value || value.toLowerCase() === 'unknown') return 'Others';
        return value;
    };
    const totals = (Array.isArray(chart) ? chart : []).reduce((acc, item) => {
        const label = normalizeGeneration(item.generation);
        acc[label] = (acc[label] || 0) + (Number(item.count) || 0);
        return acc;
    }, {});
    return Object.entries(totals)
        .map(([label, value]) => ({ id: label, label, value }))
        .filter((item) => item.value > 0);
};

function TvlaReport() {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const classes = useStyles();

    const today = useMemo(() => moment().format('YYYY-MM-DD'), []);

    // Date range + bounds. Initialised empty; populated from the server's
    // default_from_date / default_to_date / min_from_date / max_to_date on first load.
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [minFromDate, setMinFromDate] = useState(MIN_FROM_DATE);
    const [maxToDate, setMaxToDate] = useState(today);

    const [chartData, setChartData] = useState([]);
    const [note, setNote] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const applyResponse = useCallback((data) => {
        const payload = data || {};
        setChartData(toChartData(payload.chart));
        setNote(payload.note || '');
        if (payload.from_date) setFromDate(payload.from_date);
        if (payload.to_date) setToDate(payload.to_date);
        if (payload.min_from_date) setMinFromDate(payload.min_from_date);
        if (payload.max_to_date) setMaxToDate(payload.max_to_date);
        setHasLoaded(true);
    }, []);

    // Fetch report data. On the initial load no params are sent so the server
    // returns the default range and bounds; date changes pass the chosen range.
    const fetchData = useCallback((params) => {
        setIsLoading(true);
        axios.get('/reports/tvla', {
            headers: { Authorization: authToken },
            params,
        }).then((res) => {
            applyResponse(res.data);
        }).catch((err) => {
            console.log('Error fetching TVLA report', err);
            setChartData([]);
            setNote('');
            setHasLoaded(true);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [authToken, applyResponse]);

    useEffect(() => {
        if (authToken) {
            fetchData();
        }
    }, [authToken, fetchData]);

    const handleFromChange = (e) => {
        const value = e.target.value;
        if (!value) return;
        // Enforce the minimum allowed From Date.
        let newFrom = moment(value).isBefore(moment(minFromDate)) ? minFromDate : value;
        // Keep To Date on/after From Date.
        let newTo = moment(toDate).isBefore(moment(newFrom)) ? newFrom : toDate;
        setFromDate(newFrom);
        setToDate(newTo);
        fetchData({ from_date: newFrom, to_date: newTo });
    };

    const handleToChange = (e) => {
        const value = e.target.value;
        if (!value) return;
        // Enforce the maximum allowed To Date.
        const newTo = moment(value).isAfter(moment(maxToDate)) ? maxToDate : value;
        setToDate(newTo);
        fetchData({ from_date: fromDate, to_date: newTo });
    };

    const canDownload = Boolean(fromDate && toDate && chartData.length > 0);

    const handleDownload = () => {
        if (!fromDate || !toDate) return;
        setIsLoading(true);
        axios.get('/reports/tvla/download', {
            headers: { Authorization: authToken },
            params: { from_date: fromDate, to_date: toDate },
        }).then((res) => {
            if (res.status === 204 || !res.data || (Array.isArray(res.data) && res.data.length === 0)) {
                return;
            }
            const rows = Array.isArray(res.data)
                ? res.data
                : (res.data.tvla_requests || res.data.data || []);
            if (!rows.length) {
                return;
            }
            const formattedRows = rows.map((row) => ({
                'Request ID': row['Request ID'] ?? row.request_id ?? row.dsar_id ?? row.requestId ?? '',
                'Created Date': row['Created Date'] ?? row.created_date ?? row.createdDate ?? '',
                'VIN': row['VIN'] ?? row.vin ?? '',
                'Generation': row['Generation'] ?? row.generation ?? '',
            }));
            const csv = Papa.unparse(formattedRows, {
                columns: ['Request ID', 'Created Date', 'VIN', 'Generation'],
            });
            const blob = new Blob(['\ufeff', csv]);
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            const fromStamp = moment(fromDate).format('YYYYMMDD');
            const toStamp = moment(toDate).format('YYYYMMDD');
            downloadLink.download = `TVLA_${fromStamp}_${toStamp}.csv`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
        }).catch((err) => {
            console.log('Error downloading TVLA report', err);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    return (
        <div className={classes.wrapper} data-testid="tvla-report">
            {isLoading && (<LoadingIndicator />)}
            <div className={classes.headerRow}>
                <Typography variant="h6" className={classes.headerTitle}>
                    TVLA REPORT
                </Typography>
                <Tooltip title="Download TVLA details">
                    <span>
                        <IconButton
                            className={classes.downloadIcon}
                            size="small"
                            onClick={handleDownload}
                            disabled={!canDownload}
                            data-testid="tvla-download"
                        >
                            <GetAppIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </div>

            <div className={classes.filterRow}>
                <div className={classes.dateField}>
                    <label className={classes.dateLabel} htmlFor="tvla-from-date">From Date</label>
                    <input
                        id="tvla-from-date"
                        type="date"
                        className={classes.dateInput}
                        value={fromDate}
                        min={minFromDate}
                        max={toDate || maxToDate}
                        onChange={handleFromChange}
                    />
                </div>
                <div className={classes.dateField}>
                    <label className={classes.dateLabel} htmlFor="tvla-to-date">To Date</label>
                    <input
                        id="tvla-to-date"
                        type="date"
                        className={classes.dateInput}
                        value={toDate}
                        min={fromDate || minFromDate}
                        max={maxToDate}
                        onChange={handleToChange}
                    />
                </div>
            </div>

            <div className={classes.chartWrapper}>
                <TvlaPieChart data={chartData} />
            </div>

            {hasLoaded && note && (
                <div className={classes.note}>{note}</div>
            )}
        </div>
    );
}

export default TvlaReport;

